/**
 * useVoiceMuteControl - Shared mute toggle for legacy and live voice calls
 *
 * Keeps the mute UI state and side effects separate from App.tsx while
 * preserving the existing WebRTC and mobile socket behavior.
 *
 * Mute fix (Jul 28 2026): this hook is also the receiver for the
 * `avatar:live:setMuted` window event that `AvatarCallSurface`'s in-call
 * control bar dispatches. Before this, the event had NO listener anywhere
 * in the SDK — the Phase 4 mute button toggled its own UI state, dispatched
 * the event into the void, and the mic kept transmitting. The legacy
 * LiveControlDock path (which calls `toggleMute` directly) still works
 * unchanged.
 *
 * On top of the session-level mute (`currentSession.mute()` for WebRTC,
 * socket events for mobile) we also disable the shared mic stream's audio
 * tracks (`setMicStreamEnabled`). That covers live sessions with no SIP
 * destination (browser-STT via `sttFromMic`, which reuses the shared
 * stream) and is belt-and-braces for the WebRTC path.
 */

import { MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';

import CamlinSocketHandler from '../../channels/voice/CamlinSocketHandler';
import { setMicStreamEnabled } from '../../channels/voice/micStreamHolder';

interface VoiceMuteSession {
  mute?: () => void;
  unmute?: () => void;
}

export interface UseVoiceMuteControlOptions {
  currentSession: VoiceMuteSession | null;
  isMobileCall: boolean;
  isWebrtcCall: boolean;
  socketHandlerRef: MutableRefObject<CamlinSocketHandler | null>;
}

export interface UseVoiceMuteControlReturn {
  isMuted: boolean;
  toggleMute: () => void;
}

export function useVoiceMuteControl({
  currentSession,
  isMobileCall,
  isWebrtcCall,
  socketHandlerRef
}: UseVoiceMuteControlOptions): UseVoiceMuteControlReturn {
  const [isMuted, setIsMuted] = useState(false);
  const isFirstMount = useRef(true);
  const isMutedRef = useRef(false);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => !prev);
  }, []);

  // Receiver for the Phase 4 call-surface mute button. AvatarCallSurface
  // is pure presentation and can't reach the SIP session, so it dispatches
  // `avatar:live:setMuted { muted }` and we apply the actual side effects
  // here (same effect below as the legacy toggleMute path).
  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleSetMuted = (event: Event) => {
      const detail = (event as CustomEvent<{ muted?: boolean }>).detail;
      if (typeof detail?.muted === 'boolean') {
        setIsMuted(detail.muted);
      }
    };
    window.addEventListener('avatar:live:setMuted', handleSetMuted as EventListener);
    return () => {
      window.removeEventListener('avatar:live:setMuted', handleSetMuted as EventListener);
    };
  }, []);

  useEffect(() => {
    isMutedRef.current = isMuted;
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    if (isMuted) {
      if (isWebrtcCall && currentSession && typeof currentSession.mute === 'function') {
        currentSession.mute();
      } else if (isMobileCall && socketHandlerRef.current) {
        socketHandlerRef.current.sendMuteEvent();
      }
      // Always silence the shared mic stream too: it feeds both the WebRTC
      // sender and the browser-STT capture path, and stays cached across
      // calls (micStreamHolder).
      setMicStreamEnabled(false);
      return;
    }

    if (isWebrtcCall && currentSession && typeof currentSession.unmute === 'function') {
      currentSession.unmute();
    } else if (isMobileCall && socketHandlerRef.current) {
      socketHandlerRef.current.sendUnmuteEvent();
    }
    setMicStreamEnabled(true);
  }, [currentSession, isMobileCall, isMuted, isWebrtcCall, socketHandlerRef]);

  // The shared mic stream outlives this component (page-lifetime cache).
  // If we unmount while muted (avatar torn down mid-call), re-enable the
  // tracks so the next session doesn't inherit a silently-dead mic.
  useEffect(() => {
    return () => {
      if (isMutedRef.current) {
        setMicStreamEnabled(true);
      }
    };
  }, []);

  return {
    isMuted,
    toggleMute
  };
}

export default useVoiceMuteControl;
